'use client';

import classNames from 'classnames';
import { useState } from 'react';

import { UploadcareImage } from '@/src/components/UploadcareImage';
import type { Media } from '@/src/types';

import styles from './Avatar.module.scss';

export function Avatar(props: Avatar.Props) {
    const [isLoaded, setIsLoaded] = useState(false);
    const { textOnly, name, size = 'md', className } = props;

    return (
        <div
            className={classNames(
                styles.avatar,
                {
                    [styles.xs]: size === 'xs',
                    [styles.sm]: size === 'sm',
                    [styles.md]: size === 'md',
                    [styles.lg]: size === 'lg',
                    [styles.xl]: size === 'xl',
                },
                className,
            )}
        >
            {!textOnly && name && props.image?.url && (
                <UploadcareImage
                    className={styles.image}
                    alt={name}
                    src={props.image.url}
                    width={128}
                    height={128}
                    onLoad={() => setIsLoaded(true)}
                    title={name}
                />
            )}
            {typeof name === 'string' && (
                <span
                    className={classNames(styles.initials, {
                        [styles.isLoaded]: isLoaded,
                    })}
                >
                    {getInitials(name)}
                </span>
            )}
        </div>
    );
}

export namespace Avatar {
    export type Avatar =
        | {
              image?: Media;
              textOnly?: false;
              name?: string;
          }
        | {
              textOnly: true;
              name: string;
          };

    export type Props = Avatar & {
        className?: string;
        size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    };
}

function getInitials(name: string) {
    const words = name.split(' ');
    const initials = words
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();

    if (initials.length <= 1) {
        return initials.charAt(0);
    }

    return initials.charAt(0) + initials.slice(-1);
}
